import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { useDebouncedValue } from "@skilitics/designer-ui-components";
import type { NodeTemplateCategory } from "@skilitics/nautilus-endpoint-definition";

import { useContentHostId, useNodeTemplatesApi } from "../../../backend/index.ts";

const QUERY_KEY_BASE = ["node-templates"];

const useInvalidateTemplateQueries = () => {
  const queryClient = useQueryClient();

  return async ({ category }: { category?: string } = {}) => {
    if (category) {
      const categories = queryClient.getQueryData<NodeTemplateCategory[]>(CATEGORIES_QUERY_KEY);
      if (categories && !categories.find(({ name }) => name === category)) {
        await queryClient.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY, refetchType: "all" });
      }
    }
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY_BASE, refetchType: "all" });
  };
};

export const useAddNodeTemplate = () => {
  const invalidateTemplateQueries = useInvalidateTemplateQueries();
  const { add } = useNodeTemplatesApi();

  const { mutate, mutateAsync, status } = useMutation({
    mutationFn: async (payload: Parameters<typeof add>[0]) => {
      await add(payload);
      const { category } = payload;
      return { category };
    },
    onSuccess: async ({ category }) => {
      await invalidateTemplateQueries({ category });
    },
  });

  return { add: mutate, addAsync: mutateAsync, status };
};

export const useUpdateNodeTemplate = () => {
  const invalidateTemplateQueries = useInvalidateTemplateQueries();
  const { update } = useNodeTemplatesApi();

  const { mutate, mutateAsync, status } = useMutation({
    mutationFn: async ({ id, ...payload }: { id: number } & Parameters<typeof update>[1]) => {
      await update(id, payload);
      const { category } = payload;
      return { category };
    },
    onSuccess: async ({ category }) => {
      await invalidateTemplateQueries({ category });
    },
  });

  return { update: mutate, updateAsync: mutateAsync, status };
};

export const useQueryNodeTemplates = (criteria: Parameters<ReturnType<typeof useNodeTemplatesApi>["query"]>[0]) => {
  const { query } = useNodeTemplatesApi();
  const moduleId = useContentHostId();

  const fullCriteria = useMemo(
    () => ({
      ...criteria,
      moduleId,
    }),
    [criteria, moduleId],
  );

  const debouncedCriteria = useDebouncedValue(fullCriteria, 400);

  const queryKey = useMemo(
    () => [
      ...QUERY_KEY_BASE,
      ...Object.entries(debouncedCriteria).map(([name, value]) => `${name}:${JSON.stringify(value)}`),
    ],
    [debouncedCriteria],
  );

  const { data = [], status } = useQuery({
    queryKey,
    queryFn: () => query(debouncedCriteria),
  });

  return { templates: data, status };
};

export const useRemoveNodeTemplate = () => {
  const { remove } = useNodeTemplatesApi();
  const invalidateTemplateQueries = useInvalidateTemplateQueries();

  const { mutate, mutateAsync, status } = useMutation({
    mutationFn: remove,
    onSuccess: async () => {
      await invalidateTemplateQueries();
    },
  });

  return { remove: mutate, removeAsync: mutateAsync, status };
};

const CATEGORIES_QUERY_KEY = ["node-template-categories"];

export const useNodeTemplateCategories = () => {
  const { getCategories } = useNodeTemplatesApi();
  const { data = [], status } = useQuery({
    queryKey: CATEGORIES_QUERY_KEY,
    queryFn: getCategories,
  });

  return { categories: data, status };
};
